import { Pagination } from '@/domain/models'
import { Category } from '@/domain/models/content/category'
import { ContentFormat } from '@/domain/models/content/content-format'
import { ContentSearchOrder } from '@/domain/models/content/content-search-order'
import { Subtype } from '@/domain/models/content/subtype'
import { planService } from '@/data/services/plan/plan-service'
import { findPlanByName } from '@/data/services/plan/plan-service-utils'
import { requestSettingsQueryOptions } from '@/data/services/request-settings/request-settings-service'
import { getEnabledPlanIds } from '@/data/services/request-settings/request-settings-service-utils'
import { Account } from '@/domain/usecases'
import { UserContentSearch } from '@/domain/usecases/content/user-content-search'
import { parseNumber } from '@/main/helpers/number-helper'
import { getAvailablePlans } from '@/presentation/helpers/filter-plan-helper'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'
import { AVAILABLE_PLANS_QUERY_KEY } from '@/presentation/hooks/plan/use-available-plans'
import { capitalize } from '@mui/material'
import { importRemote } from '@/main/routes/helpers/loaders'
import { queryClient } from '@/infra/http'

export type ContentSearchParams = {
  searchParams: URLSearchParams
  account: Account.Model
  showFullCatalog?: boolean
  categories?: Category.Enum[]
  userSkillId?: number
  institutionId?: number
}

/**
 * Loads content search results based on search parameters
 * Used by multiple routes: search, category, skill, institution
 */
export async function loadContentSearch(params: ContentSearchParams) {
  const searchParams = await createSearchParams(params)
  const remote = await importRemote<UserContentSearch>('makeRemoteUserContentSearch', queryClient)
  const data = await remote.loader(searchParams)

  return {
    data,
    remote,
    params: searchParams
  }
}

async function createSearchParams(params: ContentSearchParams): Promise<UserContentSearch.Params> {
  const {
    searchParams,
    account,
    showFullCatalog = false,
    categories,
    userSkillId,
    institutionId
  } = params

  const queryTerm = searchParams.get(UserContentSearch.QUERY_TERM_PARAM_NAME)
  const plan = searchParams.get(UserContentSearch.PLAN_PARAM_NAME)
  const categoriesParam = searchParams.get(UserContentSearch.CATEGORIES_PARAM_NAME)
  const formats = searchParams.get(UserContentSearch.FORMATS_PARAM_NAME)
  const institutions = searchParams.get(UserContentSearch.INSTITUTIONS_PARAM_NAME)
  const languages = searchParams.get(UserContentSearch.LANGUAGES_PARAM_NAME)
  const captions = searchParams.get(UserContentSearch.CAPTIONS_PARAM_NAME)
  const subtypes =
    searchParams.get(UserContentSearch.SUBTYPES_PARAM_NAME) ?? Subtype.Enum.SUBSCRIPTION.name
  const order = searchParams.get(UserContentSearch.ORDER_PARAM_NAME)
  const page = searchParams.get(Pagination.PAGE_PARAM_NAME)
  const pageSize = searchParams.get(Pagination.PAGE_SIZE_PARAM_NAME)
  const planName = await parsePlan(plan, account, showFullCatalog)

  return {
    queryTerm: queryTerm ? capitalize(queryTerm) : undefined,
    plan: planName,
    categories: categories || parseCategories(categoriesParam),
    formats: parseFormats(formats),
    institutions: institutionId ? [institutionId] : parseInstitutions(institutions),
    languages: parseLanguagesFormat(languages),
    captions: parseLanguagesFormat(captions),
    subtypes: parseSubtypes(subtypes),
    order: parseOrder(order),
    page: parseNumber(page, Pagination.DEFAULT_PAGE)!,
    pageSize: checkFlagEnabled(FeatureFlagName.ENABLE_CAREER_MENTOR)
      ? Math.max(
          (parseNumber(pageSize, UserContentSearch.DEFAULT_PAGE_SIZE) ??
            UserContentSearch.DEFAULT_PAGE_SIZE) - 1,
          1
        )
      : parseNumber(pageSize, UserContentSearch.DEFAULT_PAGE_SIZE),
    userSkillId: userSkillId,
    showFullCatalog
  }
}

async function parsePlan(plan: string | null, account: Account.Model, showFullCatalog: boolean) {
  const companyId = account.company?.uniqueId
  const [allPlans, allowedPlanIds] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: [AVAILABLE_PLANS_QUERY_KEY],
      queryFn: () => planService.fetchAvailablePlans(),
      staleTime: 60 * 1000
    }),
    fetchAllowedPlanIds(companyId, showFullCatalog)
  ])

  const availablePlans = getAvailablePlans(allPlans, showFullCatalog, account, allowedPlanIds)
  return findPlanByName(availablePlans, plan) ?? availablePlans[0]
}

async function fetchAllowedPlanIds(
  companyId: string | undefined,
  showFullCatalog: boolean
): Promise<number[] | undefined> {
  if (!companyId) return undefined

  const request = queryClient.fetchQuery(requestSettingsQueryOptions(companyId))
  if (showFullCatalog) {
    return getEnabledPlanIds((await request)?.plans)
  }

  // Na busca normal, falha nos settings não pode quebrar a busca: sem eles, não restringe pela empresa.
  const settings = await request.catch(() => undefined)
  return settings ? getEnabledPlanIds(settings.upgradePlans) : undefined
}

function parseCategories(categories: string | null): Category.Enum[] {
  if (categories && categories.length) {
    return categories.split(',').map(Category.getByName)
  }
  return []
}

function parseFormats(formats: string | null): ContentFormat.Enum[] {
  if (formats && formats.length) {
    return formats.split(',').map(ContentFormat.getByName)
  }
  return []
}

function parseInstitutions(institutions: string | null): number[] {
  if (institutions && institutions.length) {
    return institutions.split(',').map(Number)
  }
  return []
}

function parseLanguagesFormat(languagesFormat: string | null): string[] {
  if (languagesFormat && languagesFormat.length) {
    return languagesFormat.split(',')
  }
  return []
}

function parseSubtypes(subtypes: string | null): Subtype.Enum[] {
  if (!subtypes || subtypes === 'none') {
    return []
  }
  return subtypes.split(',').map(Subtype.getByName)
}

function parseOrder(order: string | null): ContentSearchOrder.Enum {
  if (order && order.length) {
    const parsed = ContentSearchOrder.getByName(order)
    return parsed || ContentSearchOrder.Enum.RECOMMENDED
  }
  return ContentSearchOrder.Enum.RECOMMENDED
}
